import { snake2Camel } from './strings'

type ValidObjectKeyType = string | number | symbol // or a template literal

export type UsualObject = {
  [key: string]: any
}

/**
 * Is a passed in object is of type Object strictly? (no arrays!)
 * @param obj Object to check
 * @returns bool
 */
export function isStrictlyObject(obj: UsualObject): boolean {
  return Object.prototype.toString.call(obj) === '[object Object]'
}

/**
 * Translate keys of an object. Goes recursively, can parse arrays.
 * @param input an entity to translate
 * @returns a translated entity
 * @todo ensure the performance is not wack because of reducer and shouldGoDeeper being redefined for each layer.
 */
export function keys2Camel(input: any): any {
  // helper to determine, whether it should continue drilling a field (checks, whether its value is an object or an array)
  function shouldGoDeeper(input: any): boolean {
    const should = isStrictlyObject(input) || Array.isArray(input)
    return should
  }

  // reducer for substituting keys, drills on if necessary
  function reducer(previous: UsualObject, [currentKey, currentValue]: [string, any]): UsualObject {
    let possiblyNewValue = currentValue // default

    if (shouldGoDeeper(currentValue)) {
      possiblyNewValue = keys2Camel(currentValue)
    }

    previous[snake2Camel(currentKey)] = possiblyNewValue

    return previous
  }

  // SCENARIO A: an Object is given, use reducer to substitute keys
  if (isStrictlyObject(input)) {
    const entries = Object.entries(input)

    return entries.reduce(reducer, {} as UsualObject)
  }

  // SCENARIO B: an Array is given, check if values' types require drilling on
  if (Array.isArray(input)) {
    const mapped = input.map((el: any) => {
      if (shouldGoDeeper(el)) {
        return keys2Camel(el)
      } else {
        return el
      }
    })
    return mapped
  }

  // SCENARIO C: a thing of a different type is given, return it
  else {
    return input
  }
}

/**
 * Take a list of objects (usually firebase documents) and a property name.
 * Return an object with list elements grouped by the property's value.
 * @param list a list of similar objects
 * @param property a property, value of which will be used to index objects
 * @returns an object, keys of which represent
 */
export function groupPerPropertyValue<T extends UsualObject>(
  list: T[],
  property: string,
): { [key: ValidObjectKeyType]: T[] } {
  const result: { [key: ValidObjectKeyType]: T[] } = {}
  for (const element of list) {
    const value = element[property]
    if (!result[value]) {
      result[value] = []
    }
    result[value].push(element)
  }
  return result
}
